import React, { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, UserCog, ShieldCheck, Copy, Check, Eye, EyeOff } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { usersApi, formatApiError } from "../lib/api";

function activationUrl(token) {
  return `${window.location.origin}/aktivera/${token}`;
}

export default function UsersAdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", name: "", role: "technician", email: "" });
  const [activationLink, setActivationLink] = useState(null);  // {url, username, expiresMin}
  const [copied, setCopied] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setLoadError("");
    try {
      setUsers(await usersApi.list());
    } catch (e) {
      // Keep the previous users snapshot; mark the load as failed so the
      // UI never renders a false "Inga användare" empty state. The axios
      // interceptor already handles 401 by broadcasting session-lost; we
      // only surface a toast + inline message for other failures.
      const msg = formatApiError(e);
      setLoadError(msg);
      if (e?.response?.status !== 401) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = await usersApi.create(newUser);
      setActivationLink({
        url: activationUrl(data.activation_token),
        username: data.username,
        expiresMin: data.activation_expires_minutes,
      });
      setShowNew(false);
      setNewUser({ username: "", name: "", role: "technician", email: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleToggleActive = async (u) => {
    try {
      await usersApi.update(u.id, { active: !u.active });
      toast.success(u.active ? `${u.username} inaktiverad` : `${u.username} aktiverad`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReset = async (u) => {
    if (!window.confirm(`Återställa PIN-kod för ${u.username}? Alla aktiva sessioner avslutas.`)) return;
    try {
      const data = await usersApi.resetPassword(u.id);
      setActivationLink({
        url: activationUrl(data.activation_token),
        username: data.username,
        expiresMin: data.activation_expires_minutes,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Ta bort ${u.username}? Ärenden som denne skapat finns kvar.`)) return;
    try { await usersApi.remove(u.id); toast.success("Borttagen"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(activationLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Kunde inte kopiera."); }
  };

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24 fade-in-up">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center rounded-xl p-2.5"
                style={{ backgroundColor: "#3B9EDA" }}>
            <UserCog className="h-5 w-5 text-white" />
          </span>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold display-tight"
                style={{ fontFamily: "Manrope, sans-serif", color: "#0F172A" }}
                data-testid="users-admin-title">
              Admin · Användare
            </h1>
            <p className="text-slate-500 text-sm mt-1.5">
              Skapa, inaktivera och återställ konton. PIN-koden sätts av användaren själv via engångslänk.
            </p>
          </div>
        </div>
        <Button data-testid="btn-new-user" onClick={() => setShowNew(!showNew)}
                className="press-feedback rounded-full px-5 text-white min-h-[44px]"
                style={{ backgroundColor: "#3B9EDA" }}>
          <Plus className="h-4 w-4 mr-1.5" /> Ny användare
        </Button>
      </div>

      {activationLink && (
        <div data-testid="activation-link-card"
             className="soft-card p-5 mb-6 border-2 border-amber-200 bg-amber-50">
          <h3 className="font-bold text-amber-900 mb-1">
            Aktiveringslänk för {activationLink.username}
          </h3>
          <p className="text-sm text-amber-800 mb-3">
            Giltig i {activationLink.expiresMin} min · Engångsanvändning · Skicka till användaren via säker kanal (SMS/muntligt).
          </p>
          <div className="flex items-center gap-2">
            <input readOnly data-testid="activation-link-value"
                   type={showLink ? "text" : "password"}
                   value={activationLink.url}
                   onFocus={(e)=>e.target.select()}
                   className="flex-1 rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-mono" />
            <button onClick={()=>setShowLink(!showLink)} className="p-2 text-amber-800 hover:text-amber-900"
                    aria-label={showLink ? "Dölj länk" : "Visa länk"}>
              {showLink ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button onClick={copyLink} data-testid="copy-activation-link"
                    className="p-2 text-amber-800 hover:text-amber-900"
                    aria-label="Kopiera länk">
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
            <button onClick={()=>setActivationLink(null)}
                    className="text-sm text-amber-800 hover:underline ml-2">Dölj</button>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={handleCreate} data-testid="form-new-user"
              className="soft-card p-6 mb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Användarnamn *</Label>
              <Input value={newUser.username}
                     onChange={(e)=>setNewUser({...newUser, username: e.target.value.toLowerCase()})}
                     placeholder="radhika" required pattern="[a-z0-9_\-\.]{2,32}"
                     autoComplete="off" data-testid="input-new-username" />
            </div>
            <div className="space-y-1.5">
              <Label>Visningsnamn *</Label>
              <Input value={newUser.name}
                     onChange={(e)=>setNewUser({...newUser, name: e.target.value})}
                     placeholder="Radhika" required data-testid="input-new-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Roll</Label>
              <Select value={newUser.role} onValueChange={(v)=>setNewUser({...newUser, role: v})}>
                <SelectTrigger data-testid="select-new-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technician">Tekniker</SelectItem>
                  <SelectItem value="admin">Administratör</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>E-post (valfri)</Label>
              <Input type="email" value={newUser.email}
                     onChange={(e)=>setNewUser({...newUser, email: e.target.value})}
                     placeholder="valfritt@valfrimobil.se" data-testid="input-new-email" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="rounded-full px-6 text-white font-bold"
                    style={{ backgroundColor: "#3B9EDA" }}>
              Skapa + generera aktiveringslänk
            </Button>
            <Button type="button" variant="outline" className="rounded-full px-5"
                    onClick={() => setShowNew(false)}>Avbryt</Button>
          </div>
        </form>
      )}

      <div className="soft-card p-6">
        {loading ? (
          <div className="text-center py-10 text-slate-400">Laddar…</div>
        ) : loadError ? (
          <div className="text-center py-10 space-y-3" data-testid="users-load-error">
            <div className="text-red-600 font-medium">
              Kunde inte hämta användare.
            </div>
            <div className="text-xs text-slate-500">{loadError}</div>
            <Button size="sm" variant="outline" className="rounded-full"
                    onClick={refresh} data-testid="btn-users-retry">
              Försök igen
            </Button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 text-slate-500">Inga användare.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="users-list">
            {users.map((u) => (
              <li key={u.id} data-testid={`user-row-${u.username}`}
                  className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-9 w-9 rounded-full inline-flex items-center justify-center font-bold text-sm ${
                    u.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-[#E6F1FB] text-[#0C447C]"}`}>
                    {u.username.slice(0,2).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800 truncate">{u.name}</span>
                      {u.role === "admin" && <ShieldCheck className="h-4 w-4 text-indigo-600" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      @{u.username} · {u.role === "admin" ? "Administratör" : "Tekniker"}
                      {u.status === "pending_activation" && (
                        <span data-testid={`status-pending-${u.username}`}
                              className="ml-2 text-amber-700 font-medium">
                          Väntar på aktivering
                        </span>
                      )}
                      {u.status === "inactive" && (
                        <span data-testid={`status-inactive-${u.username}`}
                              className="ml-2 text-red-600 font-medium">
                          Inaktiverad
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid={`btn-reset-${u.username}`}
                          onClick={() => handleReset(u)}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Återställ PIN-kod
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid={`btn-toggle-${u.username}`}
                          onClick={() => handleToggleActive(u)}>
                    {u.active ? "Inaktivera" : "Aktivera"}
                  </Button>
                  <Button size="sm" variant="outline"
                          data-testid={`btn-delete-${u.username}`}
                          onClick={() => handleDelete(u)}
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
